import { z } from "zod";
import { zql } from "../schema";
import {
  applyBuiltMessage,
  buildMessage,
  type BuiltMessage,
  type MutateSurface,
} from "../message-builders";
import { isThreadable } from "../message-predicates";
import type { MessageKind } from "../message-payload";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { generateId } from "./helpers";
import type { MutatorCtx } from "./messages-cores";

// Call-event / system-log / thread-creation / mark-channel-read cores. Like
// the message cores, these are called by the thin `actions.*` wrappers
// (actions.ts) and re-used by the server-mutator extension. Schemas co-located
// with their cores.

export const actionSendCallEventSchema = z.object({
  channelId: z.string(),
  userId: z.string(),
  eventType: z.enum(["started_video_call", "started_audio_call", "call_ended"]),
});

export const actionPostSystemLogSchema = z.object({
  channelId: z.string(),
  actorUserId: z.string(),
  content: z.string(),
});

/**
 * Create a thread rooted at `messageId`. The action posts a system
 * message in the channel ("X started a thread on this message")
 * which serves as both an audit trail and the discovery mechanism
 * for other users. Thread replies subsequently route via
 * `thread_id = messageId` through `messageAddReplyCore` thread mode.
 *
 * `clientSystemMessageId` is generated client-side so the optimistic
 * insert and the server insert share the same ID — the second write
 * idempotently no-ops via PK conflict.
 */
export const actionCreateThreadSchema = z.object({
  messageId: z.string(),
  clientSystemMessageId: z.string(),
});

// Canonical mark-read schema. Optional `lastReadAt` lets callers pass
// explicit timestamps when they have rewind protection (Math.max),
// batch-shared timestamps, or notification-derived cursors. Defaults
// to Date.now() in the core. Server-mutator extension fans out to
// `user_channel_stats.last_read_at` (the canonical home post-P6-D1).
export const markChannelReadSchema = z.object({
  membershipId: z.string(),
  lastReadAt: z.number().optional(),
});

export async function actionSendCallEventCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionSendCallEventSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage }> {
  if (ctx?.sub && args.userId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  if (tx.location === "server") {
    // Spaces (channels.type='space') are non-message containers. The
    // membership check below would pass for space members; without
    // this type guard, hits the DB-layer trigger as a generic 500.
    // Round 4 (paired with sendMessage / replyToThread / addReply).
    const channel = await tx.run(
      zql.channels.where("id", args.channelId).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", args.channelId)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }
  const kindMap = {
    started_video_call: "call_started_video",
    started_audio_call: "call_started_audio",
    call_ended: "call_ended",
  } as const;
  const contentMap: Record<string, string> = {
    started_video_call: "started a call",
    started_audio_call: "started a call",
    call_ended: "Call ended",
  };
  const now = Date.now();
  // Kind is derived from the `eventType` enum (3 fixed values) — NOT
  // from free-form client input — so there's no spoof vector to gate
  // against, and the optimistic client branch must be allowed to run
  // (Zero invokes the client branch first on every zero.mutate call).
  // DO NOT call assertClientMaySetKind here; it's for user-input kind
  // paths (messages.insert / addReply / sendMessage / replyToThread).
  const kind = kindMap[args.eventType];

  // Best-effort participant roster — includes at least the initiator.
  // Proper roster population waits on a call-state query surface.
  const participants = [{ user_id: args.userId, joined_at: now }];

  const built = buildMessage({
    id: generateId(),
    channel_id: args.channelId,
    user_id: args.userId,
    content: contentMap[args.eventType],
    kind,
    payload: { participants },
    created_at: now,
    updated_at: now,
    reply_count: 0,
    is_broadcast: false,
    is_edited: false,
  });
  await applyBuiltMessage(tx, built);
  return { built };
}

export async function actionPostSystemLogCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionPostSystemLogSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage }> {
  if (ctx?.sub && args.actorUserId !== ctx.sub) {
    throw new Error("Unauthorized: userId mismatch");
  }
  // tx.mutate bypasses table auth, so enforce channel membership here
  if (tx.location === "server") {
    // Spaces (channels.type='space') reject system-log writes via
    // the DB trigger; surface a friendlier error here. Round 4.
    const channel = await tx.run(
      zql.channels.where("id", args.channelId).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot receive messages");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", args.channelId)
        .where("user_id", ctx?.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }
  // Kind is hardcoded to 'system' — no client input, no spoof vector.
  // DO NOT call assertClientMaySetKind (the optimistic client branch
  // must run; Zero invokes it first on every zero.mutate call).
  const now = Date.now();
  const built = buildMessage({
    id: generateId(),
    channel_id: args.channelId,
    user_id: "system-bot",
    content: args.content,
    kind: "system",
    is_edited: false,
    is_broadcast: false,
    reply_count: 0,
    created_at: now,
    updated_at: now,
  });
  await applyBuiltMessage(tx, built);
  return { built };
}

/**
 * Create a thread rooted at a channel message.
 *
 * Writes a `kind="system"` message into the channel announcing the
 * thread ("X started a thread on this message") with payload
 * `{ event: "thread_created", thread_message_id }`. The system
 * message is the audit/discovery surface; the source message itself
 * is unchanged.
 *
 * Auth: any channel member (creating a thread is a low-privilege
 * action; anyone who can post in the channel can start a thread on
 * any message). Note: spaces (channels.type='space') and threads on
 * thread replies (`source.thread_id != null`) are rejected.
 *
 * Idempotent via the client-supplied `clientSystemMessageId` — Zero's
 * insert PK conflict is a silent no-op so re-trigger is safe.
 *
 * `kind="system"` is a `SERVER_ONLY_KIND` — `assertClientMaySetKind`
 * would normally reject the optimistic client branch. We deliberately
 * skip that check here (mirrors `actionPostSystemLogCore`) because
 * the kind is hardcoded by this action, not caller-supplied.
 */
export async function actionCreateThreadCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof actionCreateThreadSchema>;
  ctx: MutatorCtx;
}): Promise<{ built: BuiltMessage } | null> {
  // Defense-in-depth: callers must supply two distinct UUIDs. If the
  // same UUID is sent for both, the idempotent-retry lookup below
  // would match the source row itself and silently no-op without ever
  // posting the announcement.
  if (args.clientSystemMessageId === args.messageId) {
    throw new Error(
      "Unauthorized: clientSystemMessageId must differ from messageId",
    );
  }
  // Idempotent retry: if a row with the supplied client-generated id
  // already exists AND it's the system announcement we'd post (kind +
  // payload.thread_message_id match), treat as replay → no-op.
  // Validating the row shape (not just the id) prevents a UUID collision
  // with an unrelated row from silently swallowing the request.
  const existingSystemRow = await tx.run(
    zql.messages.where("id", args.clientSystemMessageId).one(),
  );
  if (existingSystemRow) {
    const payload = existingSystemRow.payload as
      | { event?: unknown; thread_message_id?: unknown }
      | null
      | undefined;
    const isReplayOfSameThreadCreation =
      existingSystemRow.kind === "system" &&
      payload?.event === "thread_created" &&
      payload?.thread_message_id === args.messageId;
    if (!isReplayOfSameThreadCreation) {
      throw new Error(`Message ID conflict: ${args.clientSystemMessageId}`);
    }
    return null;
  }

  // Read source first — needed for both the auth checks and the
  // channel_id stamp on the system message. Single fetch, reused below.
  const source = await tx.run(zql.messages.where("id", args.messageId).one());
  if (!source) {
    if (tx.location === "server") {
      throw new Error("Source message not found");
    }
    return null; // optimistic client without cache — server will produce on retry
  }

  if (tx.location === "server") {
    if (!ctx?.sub) {
      throw new Error("Unauthorized: missing caller identity");
    }
    // Reject creating a thread on a thread reply — threads root at
    // first-class channel messages only.
    if (source.thread_id != null) {
      throw new Error("Unauthorized: cannot create a thread on a thread reply");
    }
    // Reject non-threadable kinds (system / call_started_* / call_ended).
    // The desktop hover bar and iOS overlay also gate on this, but
    // the server is the source of truth — a buggy or malicious client
    // can't create a thread rooted at a call event or another system
    // announcement.
    if (!isThreadable(source.kind as MessageKind)) {
      throw new Error("Unauthorized: kind is not threadable");
    }
    // Channel membership check — anyone in the channel can start a thread.
    const channel = await tx.run(
      zql.channels.where("id", source.channel_id).one(),
    );
    if (channel?.type === "space") {
      throw new Error("Unauthorized: spaces cannot host threads");
    }
    const membership = await tx.run(
      zql.channel_members
        .where("channel_id", source.channel_id)
        .where("user_id", ctx.sub)
        .where("removed_at", "IS", null)
        .one(),
    );
    if (!membership) {
      throw new Error("Unauthorized: not a channel member");
    }
  }

  const now = Date.now();
  // The system message body is mostly cosmetic — desktop / iOS render
  // a typed callout from `payload.event === "thread_created"` rather
  // than the raw content. The string survives as a fallback for any
  // surface that hasn't learned the typed render (e.g. legacy clients).
  const built = buildMessage({
    id: args.clientSystemMessageId,
    channel_id: source.channel_id,
    user_id: "system-bot",
    content: "started a thread",
    kind: "system",
    payload: {
      event: "thread_created",
      thread_message_id: args.messageId,
      started_by_user_id: ctx?.sub ?? "system-bot",
    },
    is_edited: false,
    is_broadcast: false,
    reply_count: 0,
    created_at: now,
    updated_at: now,
  });
  await applyBuiltMessage(tx, built);
  return { built };
}

// markChannelRead core. The shared body runs on both client and server.
// Post-P6-D1: `channel_members.last_read_at` was dropped from the Zero
// schema; canonical home is `user_channel_stats.last_read_at`, written
// by the server-mutator extension in `server/server-mutators.ts` via
// raw SQL (ucs is in `serverOnlyTables`, blocking tx.mutate).
//
// We do NOT call `tx.mutate.channel_members.update({last_read_at})`
// here — Zero's PushProcessor rejects mutations that touch columns
// missing from the schema, which fails the entire mutator and prevents
// the server-mutator extension from running. That regression broke all
// mark-read flows including inbox "Clear all" (Sentry: ApplicationError:
// "Column last_read_at was not found in the Zero schema for the table
// channel_members"). Optimistic UI for read state is driven by
// `useLastReadMap`, which reads from ucs and updates as soon as
// replication delivers the server's raw-SQL write.
//
// Returns the resolved `lastReadAt` so the server-mutator extension
// can write the same timestamp to ucs without re-deriving it.
export async function markChannelReadCore({
  tx,
  args,
  ctx,
}: {
  tx: MutateSurface;
  args: z.infer<typeof markChannelReadSchema>;
  ctx: MutatorCtx;
}): Promise<{ lastReadAt: number; userId: string; channelId: string } | null> {
  const lastReadAt = args.lastReadAt ?? Date.now();

  if (tx.location === "server") {
    const membership = await tx.run(
      zql.channel_members.where("id", args.membershipId).one(),
    );
    if (!membership) return null;
    if (!ctx?.sub) {
      throw new Error("Unauthorized: missing caller identity");
    }
    if (membership.user_id !== ctx.sub) {
      throw new Error("Unauthorized: can only mark own memberships as read");
    }
    return {
      lastReadAt,
      userId: membership.user_id as string,
      channelId: membership.channel_id as string,
    };
  }

  // Client path — no optimistic mutate. Read state surfaces through
  // `useLastReadMap` (sourced from user_channel_stats), which the
  // server-mutator extension updates via raw SQL.
  return null;
}
